"use client";

import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface CodeBlockProps {
  code: string;
  language?: string;
  title?: string;
  className?: string;
  showLineNumbers?: boolean;
}

export function CodeBlock({
  code,
  language,
  title,
  className,
  showLineNumbers = false,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn("rounded-xl overflow-hidden bg-[#1a1a2e] border border-border", className)}>
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-[#16162a] border-b border-border/50">
          <span className="text-xs font-medium text-white/60 uppercase tracking-wider">
            {title}
          </span>
          <button
            onClick={handleCopy}
            className="p-1.5 rounded-md text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
            aria-label={copied ? "Copied" : "Copy code"}
          >
            {copied ? (
              <Check className="w-4 h-4 text-success" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
      )}
      <div className="relative group">
        {!title && (
          <button
            onClick={handleCopy}
            className="absolute top-3 right-3 p-2 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
            aria-label={copied ? "Copied" : "Copy code"}
          >
            {copied ? (
              <Check className="w-4 h-4 text-success" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        )}
        <pre className="p-4 overflow-x-auto">
          <code className={cn("text-sm font-mono text-white/90", showLineNumbers && "block")}>
            {showLineNumbers
              ? code.split("\n").map((line, i) => (
                  <span key={i} className="block">
                    <span className="inline-block w-8 text-white/30 select-none">
                      {i + 1}
                    </span>
                    {line}
                  </span>
                ))
              : code}
          </code>
        </pre>
      </div>
    </div>
  );
}

// Inline code component for inline syntax highlighting
interface InlineCodeProps {
  children: React.ReactNode;
  className?: string;
}

export function InlineCode({ children, className }: InlineCodeProps) {
  return (
    <code
      className={cn(
        "px-1.5 py-0.5 rounded-md bg-bg-alt text-primary text-sm font-mono",
        className
      )}
    >
      {children}
    </code>
  );
}
